import { useState, type CSSProperties } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { deliveriesAPI } from '../../api/deliveries'
import type { Delivery, TimelineEntry } from '../../types'
import DashIcon from '../../components/ui/DashIcon'
import DashAvatar from '../../components/ui/DashAvatar'
import StatusBadge from '../../components/ui/StatusBadge'
import { ColisQRCode } from '../../components/expediteur/ColisQRCode'
import { CancelModal } from '../../components/livreur/modals/CancelModal'
import { CommentModal } from '../../components/livreur/modals/CommentModal'
import { RetardModal } from '../../components/livreur/modals/RetardModal'
import { InjoinableModal } from '../../components/livreur/modals/InjoinableModal'
import { EchecModal } from '../../components/livreur/modals/EchecModal'
import { ConfirmerRetourModal } from '../../components/livreur/modals/ConfirmerRetourModal'

// ─── Constants ─────────────────────────────────────────────────────────────────

const TRACKING_STEPS = [
  { statuses: ['pending', 'validated', 'matching'], label: 'Commande créée',                pending: 'Création de la commande' },
  { statuses: ['accepted', 'assigned'],              label: 'Livreur trouvé',                pending: 'En attente d\'un livreur' },
  { statuses: ['picked_up'],                         label: 'Récupéré par le livreur',       pending: 'Ramassage à planifier' },
  { statuses: ['in_transit'],                        label: 'En route vers le destinataire', pending: 'Transit en cours' },
  { statuses: ['delivered'],                         label: 'Livré',                         pending: 'Signature + photo requis' },
]

const STATUS_LABEL: Record<string, string> = {
  pending: 'Commande créée', validated: 'Validé', matching: 'Matching livreur',
  accepted: 'Livreur trouvé', assigned: 'Livreur assigné', picked_up: 'Récupéré',
  in_transit: 'En route', delivered: 'Livré', failed: 'Échec de livraison',
  cancelled: 'Annulé', returned: 'Retourné', unreachable: 'Client injoinable',
  return_pending: 'Retour en cours', rescheduled: 'Reprogrammé',
}

const SPECIAL_BANNERS: Record<string, { bg: string; border: string; color: string; message: string }> = {
  failed: {
    bg: '#FEF2F2', border: '#FCA5A5', color: '#DC2626',
    message: 'Colis en échec — en attente de décision admin (reprogrammation ou retour)',
  },
  rescheduled: {
    bg: '#EFF6FF', border: '#BFDBFE', color: '#1D4ED8',
    message: 'Reprogrammé — nouvelle tentative planifiée par l\'admin',
  },
  return_pending: {
    bg: '#FFFBEB', border: '#FDE68A', color: '#92400E',
    message: 'Retour demandé — ramener ce colis à l\'entrepôt / chez l\'expéditeur',
  },
  returned: {
    bg: '#F5F3FF', border: '#DDD6FE', color: '#6D28D9',
    message: 'Colis retourné avec succès',
  },
}

const SIZE_LABEL: Record<string, string> = {
  small: 'Petit', medium: 'Moyen', large: 'Grand', extra: 'Extra-large',
}

const STATUS_ACTIONS: Record<string, { label: string; next: string; color: string; icon: string; modal?: string }[]> = {
  accepted:   [{ label: 'Marquer récupéré',      next: 'picked_up',  color: '#F59E0B', icon: 'package' }],
  assigned:   [{ label: 'Marquer récupéré',      next: 'picked_up',  color: '#F59E0B', icon: 'package' }],
  picked_up:  [{ label: 'Démarrer la livraison', next: 'in_transit', color: '#3B82F6', icon: 'truck'   }],
  in_transit: [
    { label: 'Livraison effectuée', next: 'delivered', color: '#10B981', icon: 'check' },
    { label: 'Signaler un échec',   next: 'failed',    color: '#EF4444', icon: 'x',    modal: 'echec' },
  ],
  rescheduled: [
    { label: 'Livraison effectuée', next: 'delivered', color: '#10B981', icon: 'check' },
    { label: 'Signaler un échec',   next: 'failed',    color: '#EF4444', icon: 'x',    modal: 'echec' },
  ],
  return_pending: [
    { label: 'Confirmer le retour', next: 'returned', color: '#F59E0B', icon: 'package', modal: 'retour' },
  ],
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

const fmtRelative = (iso: string) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'à l\'instant'
  if (mins < 60) return `il y a ${mins} min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `il y a ${hrs}h`
  return `il y a ${Math.floor(hrs / 24)}j`
}

function stepIndexFor(status: string): number {
  for (let i = 0; i < TRACKING_STEPS.length; i++) {
    if (TRACKING_STEPS[i].statuses.includes(status)) return i
  }
  return -1
}

function timelineEntryFor(timeline: TimelineEntry[], statuses: string[]): TimelineEntry | undefined {
  return [...timeline].reverse().find(e => statuses.includes(e.status))
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, value, sub, green, strong }: {
  label: string; value?: string | null; sub?: string | null; green?: boolean; strong?: boolean
}) {
  if (!value) return null
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '10px 0', borderBottom: '1px solid #F3F4F6',
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0, marginRight: 24, paddingTop: 2 }}>
        {label}
      </span>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, fontWeight: strong ? 700 : 500, color: green ? '#059669' : strong ? '#0D1117' : '#374151' }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  )
}

function InfoChip({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1117' }}>{value ?? '—'}</div>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export const ParcelDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: delivery, isLoading } = useQuery<Delivery>({
    queryKey: ['delivery', id],
    queryFn: () => deliveriesAPI.getById(id!),
    enabled: !!id,
    refetchInterval: 30_000,
  })

  const [showCancel,     setShowCancel]     = useState(false)
  const [showComment,    setShowComment]    = useState(false)
  const [showRetard,     setShowRetard]     = useState(false)
  const [showInjoinable, setShowInjoinable] = useState(false)
  const [showEchec,      setShowEchec]      = useState(false)
  const [showRetour,     setShowRetour]     = useState(false)
  const [statusLoading,  setStatusLoading]  = useState(false)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['delivery', id] })
    queryClient.invalidateQueries({ queryKey: ['deliveries'] })
  }

  const handleStatusChange = async (next: string) => {
    if (!id) return
    setStatusLoading(true)
    try {
      await deliveriesAPI.updateStatus(id, { status: next })
      toast.success('Statut mis à jour')
      invalidate()
    } catch {
      toast.error('Erreur lors de la mise à jour')
    } finally {
      setStatusLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <span style={{ color: '#9CA3AF', fontSize: 14 }}>Chargement…</span>
      </div>
    )
  }

  if (!delivery) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <DashIcon name="alert" size={32} style={{ color: '#EF4444' }} />
        <p style={{ color: '#EF4444', marginTop: 12 }}>Colis introuvable.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/livreur/colis')}>← Retour</button>
      </div>
    )
  }

  // ── Derived data ─────────────────────────────────────────────────────────────

  type PopulatedExpediteur = { _id?: string; name?: string; phone?: string; company?: { name?: string } }
  const exped = delivery.expediteur as unknown as PopulatedExpediteur | undefined
  const expedName = exped?.company?.name ?? exped?.name ?? '—'

  const timeline     = delivery.timeline ?? []
  const currentIdx   = stepIndexFor(delivery.status)
  const isTerminal   = ['delivered', 'cancelled', 'failed', 'returned', 'unreachable'].includes(delivery.status)
  const isNegative   = ['cancelled', 'failed', 'returned', 'unreachable', 'return_pending', 'rescheduled'].includes(delivery.status)
  const availActions = STATUS_ACTIONS[delivery.status] ?? []
  const specialBanner = SPECIAL_BANNERS[delivery.status]
  const failureInfo  = (delivery as any).failureInfo as NonNullable<typeof delivery['failureInfo']> | undefined

  return (
    <>
      {/* Modals */}
      {showCancel && (
        <CancelModal deliveryId={delivery._id} trackingCode={delivery.trackingCode}
          onClose={() => setShowCancel(false)} onSuccess={invalidate} />
      )}
      {showComment && (
        <CommentModal deliveryId={delivery._id}
          onClose={() => setShowComment(false)} onSuccess={invalidate} />
      )}
      {showRetard && (
        <RetardModal deliveryId={delivery._id}
          onClose={() => setShowRetard(false)} onSuccess={invalidate} />
      )}
      {showInjoinable && (
        <InjoinableModal deliveryId={delivery._id}
          onClose={() => setShowInjoinable(false)} onSuccess={invalidate} />
      )}
      {showEchec && (
        <EchecModal
          deliveryId={delivery._id}
          trackingCode={delivery.trackingCode}
          currentAttempts={failureInfo?.attempts ?? 0}
          maxAttempts={failureInfo?.maxAttempts ?? 3}
          onClose={() => setShowEchec(false)}
          onSuccess={invalidate}
        />
      )}
      {showRetour && (
        <ConfirmerRetourModal
          deliveryId={delivery._id}
          trackingCode={delivery.trackingCode}
          onClose={() => setShowRetour(false)}
          onSuccess={invalidate}
        />
      )}

      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <button
        onClick={() => navigate('/livreur/colis')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 5, padding: '0 0 10px', fontFamily: 'inherit' }}
      >
        <DashIcon name="arrow-left" size={13} /> Mes colis
      </button>

      <div className="page-head" style={{ marginBottom: 20 }}>
        <div className="ph-titles">
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            Colis <span style={{ color: '#1D9E75' }}>{delivery.trackingCode}</span>
            <StatusBadge status={delivery.status} />
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: 'var(--muted)' }}>
            Créé le {fmtDate(delivery.createdAt)} {fmtTime(delivery.createdAt)}
            {delivery.updatedAt && ` · Mis à jour ${fmtRelative(delivery.updatedAt)}`}
          </p>
        </div>
        {availActions.length > 0 && (
          <div className="ph-actions">
            {availActions.map((a) => (
              <button
                key={a.next}
                onClick={() => {
                  if (a.modal === 'echec') { setShowEchec(true) }
                  else if (a.modal === 'retour') { setShowRetour(true) }
                  else { handleStatusChange(a.next) }
                }}
                disabled={statusLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 10,
                  border: 'none', background: a.color, color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: statusLoading ? 'not-allowed' : 'pointer',
                  opacity: statusLoading ? 0.7 : 1, fontFamily: 'inherit',
                }}
              >
                <DashIcon name={a.icon} size={14} /> {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Top row: QR + Suivi ────────────────────────────────────────────────── */}
      <div className="detail-grid" style={{ '--detail-cols': '230px 1fr', gap: 16, marginBottom: 16 } as CSSProperties}>

        {/* QR card */}
        <div style={{
          background: '#fff', border: '2px dashed #E5E7EB', borderRadius: 16, padding: 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        }}>
          <ColisQRCode
            trackingCode={delivery.trackingCode}
            contactName={delivery.dropoff.contactName}
            dropoffAddress={delivery.dropoff.address}
            price={delivery.price?.base}
            paymentMethod={delivery.paymentMethod}
            size={160}
            showActions={false}
          />
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: '#0D1117' }}>
                {delivery.trackingCode}
              </span>
              <button
                onClick={() => { navigator.clipboard.writeText(delivery.trackingCode); toast.success('Copié !') }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 2, display: 'flex' }}
              >
                <DashIcon name="copy" size={13} />
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Code de suivi</div>
          </div>
          {delivery.paymentMethod === 'cash' && (
            <div style={{
              width: '100%', padding: '10px 14px', background: '#EFF9F5',
              border: '1px solid #A7F3D0', borderRadius: 10, textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>COD à collecter</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#059669' }}>{delivery.price?.base ?? 0} TND</div>
            </div>
          )}
        </div>

        {/* Suivi du colis */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Suivi du colis</span>
            {!isTerminal && (
              <span style={{
                background: '#EFF9F5', border: '1px solid #A7F3D0', color: '#059669',
                fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
              }}>
                En cours
              </span>
            )}
          </div>

          {isNegative ? (
            <div style={{ padding: '16px 20px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 12, color: '#DC2626' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{STATUS_LABEL[delivery.status]}</div>
              {delivery.cancellation?.reason && (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6B7280', fontWeight: 400 }}>
                  {delivery.cancellation.reason}
                </p>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 11, top: 20, bottom: 20, width: 2, background: '#E5E7EB', zIndex: 0 }} />
              {TRACKING_STEPS.map((step, idx) => {
                const isDone   = currentIdx > idx
                const isActive = currentIdx === idx
                const entry    = timelineEntryFor(timeline, step.statuses)
                return (
                  <div key={idx} style={{ display: 'flex', gap: 14, marginBottom: 20, position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isDone ? '#1D9E75' : isActive ? '#1D9E75' : '#F3F4F6',
                      border: !isDone && !isActive ? '2px solid #E5E7EB' : 'none',
                      boxShadow: isActive ? '0 0 0 4px rgba(29,158,117,0.18)' : 'none',
                      fontSize: 11, color: '#fff', fontWeight: 700,
                    }}>
                      {isDone ? '✓' : isActive ? <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} /> : null}
                    </div>
                    <div style={{ paddingTop: 2, flex: 1 }}>
                      <div style={{ fontWeight: isDone || isActive ? 700 : 400, fontSize: 13, color: isDone || isActive ? '#0D1117' : '#9CA3AF', marginBottom: 2 }}>
                        {step.label}
                      </div>
                      {entry?.note && <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 1 }}>{entry.note}</div>}
                      {entry
                        ? <div style={{ fontSize: 11, color: '#9CA3AF' }}>{fmtDate(entry.timestamp)} · {fmtTime(entry.timestamp)}</div>
                        : <div style={{ fontSize: 11, color: '#C4C9D4', fontStyle: 'italic' }}>{step.pending}</div>
                      }
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Bannière statut spécial ───────────────────────────────────────────── */}
      {specialBanner && (
        <div style={{
          background: specialBanner.bg, border: `1px solid ${specialBanner.border}`,
          borderRadius: 12, padding: '14px 18px', marginBottom: 16,
          color: specialBanner.color, fontSize: 13, fontWeight: 500,
        }}>
          {specialBanner.message}
          {delivery.status === 'rescheduled' && failureInfo?.rescheduledFor && (
            <span style={{ fontWeight: 700, marginLeft: 6 }}>
              · {new Date(failureInfo.rescheduledFor).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short' })}
            </span>
          )}
        </div>
      )}

      {/* ── Historique tentatives ─────────────────────────────────────────────── */}
      {failureInfo && (failureInfo.attempts ?? 0) > 0 && (
        <div style={{
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16,
          padding: 24, marginBottom: 16,
        }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>
            Tentatives de livraison
          </h3>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {Array.from({ length: failureInfo.maxAttempts ?? 3 }).map((_, i) => (
              <div key={i} style={{
                flex: 1, height: 8, borderRadius: 4,
                background: i < (failureInfo.attempts ?? 0) ? '#EF4444' : '#E5E7EB',
              }} />
            ))}
          </div>
          <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
            {failureInfo.attempts} / {failureInfo.maxAttempts ?? 3} tentatives
            {failureInfo.reasonLabel && <> · Dernier motif : <strong>{failureInfo.reasonLabel}</strong></>}
          </p>
          {failureInfo.note && (
            <p style={{ fontSize: 13, color: '#374151', marginTop: 8, fontStyle: 'italic' }}>
              "{failureInfo.note}"
            </p>
          )}
        </div>
      )}

      {/* ── Middle row: Détails + Client ──────────────────────────────────────── */}
      <div className="detail-grid" style={{ '--detail-cols': '1fr 380px', gap: 16, marginBottom: 16 } as CSSProperties}>

        {/* Détails du colis */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 18px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Détails du colis</h3>

          {/* Route visual */}
          <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, marginTop: 3 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#1D9E75', border: '2px solid #059669' }} />
                <div style={{ width: 2, height: 32, marginTop: 4, background: 'repeating-linear-gradient(to bottom, #9CA3AF 0, #9CA3AF 4px, transparent 4px, transparent 8px)' }} />
              </div>
              <div style={{ paddingBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1D9E75', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                  Départ · {expedName}
                </div>
                <div style={{ fontSize: 13, color: '#374151' }}>{delivery.pickup?.address ?? '—'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flexShrink: 0, marginTop: 3 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#0D1117', border: '2px solid #374151' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0D1117', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                  Destination · {delivery.dropoff.contactName ?? '—'}
                </div>
                <div style={{ fontSize: 13, color: '#374151' }}>{delivery.dropoff.address}</div>
              </div>
            </div>
          </div>

          <FieldRow label="Destinataire" value={delivery.dropoff.contactName ?? '—'} sub={delivery.dropoff.contactPhone} />
          {delivery.package?.weight && <FieldRow label="Poids" value={`${delivery.package.weight} kg`} />}
          <FieldRow label="Type" value={[
            delivery.package?.fragile ? 'Fragile' : null,
            delivery.package?.description,
          ].filter(Boolean).join(' · ') || SIZE_LABEL[delivery.package?.size ?? 'medium']} />
          {delivery.paymentMethod === 'cash' && (
            <FieldRow label="COD à collecter" value={`${delivery.price?.base ?? 0} DT`} green strong />
          )}
          <FieldRow label="Votre gain" value={`+${delivery.price?.livreurAmount ?? 0} DT`} green strong />
          {delivery.dropoff.notes && <FieldRow label="Note livraison" value={delivery.dropoff.notes} />}
          {delivery.comment && <FieldRow label="Note expéditeur" value={`« ${delivery.comment} »`} />}
        </div>

        {/* Client */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Client</h3>

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20 }}>
            <DashAvatar name={delivery.dropoff.contactName ?? 'C'} size={48} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0D1117', marginBottom: 3 }}>
                {delivery.dropoff.contactName ?? '—'}
              </div>
              <div style={{ fontSize: 13, color: '#6B7280' }}>
                {delivery.dropoff.contactPhone ?? '—'}
              </div>
            </div>
          </div>

          <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 8, marginBottom: 20 } as CSSProperties}>
            <InfoChip label="Zone" value={delivery.dropoff.address.split(',').pop()?.trim()} />
            <InfoChip label="Paiement" value={delivery.paymentMethod === 'cash' ? 'Cash' : 'En ligne'} />
            <InfoChip label="Montant" value={`${delivery.price?.base ?? 0} TND`} />
            <InfoChip label="Votre gain" value={`${delivery.price?.livreurAmount ?? 0} TND`} />
          </div>

          <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 8 } as CSSProperties}>
            <a
              href={`tel:${delivery.dropoff.contactPhone}`}
              className="btn btn-ghost"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <DashIcon name="phone" size={14} /> Appeler
            </a>
            <button
              className="btn btn-ghost"
              onClick={() => {
                const p = (delivery.dropoff.contactPhone ?? '').replace(/\D/g, '')
                window.open(
                  `https://wa.me/${p}?text=${encodeURIComponent(`Bonjour ${delivery.dropoff.contactName ?? ''}, votre colis ${delivery.trackingCode} est en cours de livraison.`)}`,
                  '_blank'
                )
              }}
            >
              <DashIcon name="message" size={14} /> WhatsApp
            </button>
          </div>

          {delivery.dropoff.notes && (
            <div style={{ marginTop: 16, padding: '12px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', marginBottom: 4 }}>Note de livraison</div>
              <div style={{ fontSize: 13, color: '#374151' }}>{delivery.dropoff.notes}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Actions ───────────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Actions</h3>
        <p style={{ margin: '0 0 20px', fontSize: 12, color: '#9CA3AF' }}>Signalement et gestion du colis.</p>
        <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 10 } as CSSProperties}>
          {([
            { icon: 'message', label: 'Commenter',         onClick: () => setShowComment(true) },
            ...(!isTerminal ? [
              { icon: 'clock',   label: 'Signaler un retard', onClick: () => setShowRetard(true) },
              { icon: 'phone',   label: 'Client injoinable',  onClick: () => setShowInjoinable(true) },
              { icon: 'x',       label: 'Annuler le colis',   onClick: () => setShowCancel(true), danger: true },
            ] : []),
          ] as { icon: string; label: string; onClick: () => void; danger?: boolean }[]).map((a) => (
            <button
              key={a.label}
              onClick={a.onClick}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
                borderRadius: 12, border: `1px solid ${a.danger ? '#FCA5A5' : '#E5E7EB'}`,
                background: a.danger ? '#FEF2F2' : '#F9FAFB',
                color: a.danger ? '#DC2626' : '#374151',
                fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
              }}
            >
              <DashIcon name={a.icon} size={15} />
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </>
  )
}
